import React, { useMemo, useState } from "react";

type SelectedRowErrorPanelProps = {
  selectedRowIndex?: number;
  errorMessages?: string[];
  srcDip?: string | null;
  srcInboundId?: string | null;
  dstDip?: string | null;
  dstOutboundId?: string | null;
};

const displayValue = (value?: string | null) => {
  const text = String(value ?? "").trim();
  return text || "-";
};

const normalizeErrorMessage = (message: string) =>
  message.replace(/^\[E\]\s*/i, "").trim();

const ErrorCountBadge: React.FC<{
  count: number;
}> = ({ count }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      height: "22px",
      padding: "0 8px",
      borderRadius: "999px",
      backgroundColor: count > 0 ? "#fef3f2" : "#f2f4f7",
      color: count > 0 ? "#b42318" : "#667085",
      fontSize: "12px",
      fontWeight: 700,
      lineHeight: 1,
      whiteSpace: "nowrap",
    }}
  >
    {count} {count === 1 ? "message" : "messages"}
  </span>
);

const InterfaceInfoItem: React.FC<{
  label: string;
  value?: string | null;
}> = ({ label, value }) => {
  const displayText = displayValue(value);

  return (
    <div
      style={{
        minWidth: 0,
      }}
    >
      <div
        style={{
          color: "#667085",
          fontSize: "10px",
          fontWeight: 700,
          lineHeight: 1.1,
          letterSpacing: "0.02em",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </div>

      <div
        title={displayText}
        style={{
          marginTop: "3px",
          color: "#111827",
          fontSize: "13px",
          fontWeight: 700,
          lineHeight: 1.15,
          whiteSpace: "nowrap",
        }}
      >
        {displayText}
      </div>
    </div>
  );
};

const InterfaceInfoLine: React.FC<{
  srcDip?: string | null;
  srcInboundId?: string | null;
  dstDip?: string | null;
  dstOutboundId?: string | null;
}> = ({ srcDip, srcInboundId, dstDip, dstOutboundId }) => {
  const hasInterfaceInfo = [srcDip, srcInboundId, dstDip, dstOutboundId].some(
    (value) => String(value ?? "").trim() !== "",
  );

  if (!hasInterfaceInfo) return null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns:
          "minmax(52px, 72px) minmax(220px, max-content) 18px minmax(52px, 72px) minmax(220px, max-content)",
        columnGap: "10px",
        alignItems: "end",
      }}
    >
      <InterfaceInfoItem label="SrcDIP" value={srcDip} />
      <InterfaceInfoItem label="SrcInboundID" value={srcInboundId} />

      <div
        aria-hidden="true"
        style={{
          paddingBottom: "1px",
          color: "#98a2b3",
          fontSize: "14px",
          fontWeight: 800,
          lineHeight: 1,
          textAlign: "center",
        }}
      >
        →
      </div>

      <InterfaceInfoItem label="DstDIP" value={dstDip} />
      <InterfaceInfoItem label="DstOutboundID" value={dstOutboundId} />
    </div>
  );
};

export const SelectedRowErrorPanel: React.FC<SelectedRowErrorPanelProps> = ({
  selectedRowIndex,
  errorMessages = [],
  srcDip,
  srcInboundId,
  dstDip,
  dstOutboundId,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const isRowSelected = selectedRowIndex !== undefined;

  const displayErrorMessages = useMemo(
    () => errorMessages.filter((message) => message.trim() !== ""),
    [errorMessages],
  );

  const errorCount = isRowSelected ? displayErrorMessages.length : 0;

  return (
    <section
      aria-label="Selected row error messages"
      style={{
        boxSizing: "border-box",
        margin: "0px 14px 10px",
        border: "1px solid #d0d7de",
        borderRadius: "10px",
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 8px rgba(16, 24, 40, 0.06)",
        color: "#111827",
      }}
    >
      <div
        style={{
          padding: "9px 12px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isRowSelected
              ? "max-content minmax(260px, 1fr) 28px"
              : "minmax(0, 1fr) 28px",
            alignItems: "center",
            columnGap: "18px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px",
              minWidth: 0,
            }}
          >
            <ErrorCountBadge count={errorCount} />
          </div>

          {isRowSelected && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                minWidth: 0,
              }}
            >
              <InterfaceInfoLine
                srcDip={srcDip}
                srcInboundId={srcInboundId}
                dstDip={dstDip}
                dstOutboundId={dstOutboundId}
              />
            </div>
          )}

          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={
              isOpen
                ? "Collapse selected row error messages"
                : "Expand selected row error messages"
            }
            onClick={() => setIsOpen((prev) => !prev)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              flexShrink: 0,
              border: "1px solid #d0d7de",
              borderRadius: "7px",
              backgroundColor: "#ffffff",
              cursor: "pointer",
              color: "#344054",
              fontSize: "14px",
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {isOpen ? "⌃" : "⌄"}
          </button>
        </div>

        {isOpen && (
          <div
            style={{
              marginTop: "8px",
              height: "92px",
              overflowY: "auto",
              boxSizing: "border-box",
              border: "1px solid #e4e7ec",
              borderRadius: "8px",
              backgroundColor: "#ffffff",
            }}
          >
            {!isRowSelected ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  padding: "0 12px",
                  boxSizing: "border-box",
                  color: "#667085",
                  fontSize: "13px",
                  lineHeight: 1.4,
                }}
              >
                Select any cell in the IFList to view the error details for that
                row here.
              </div>
            ) : (
              displayErrorMessages.map((message, index) => (
                <div
                  key={`selected-row-error-message-${index}`}
                  style={{
                    padding: "7px 10px",
                    borderTop: index === 0 ? "none" : "1px solid #eef2f6",
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#fcfcfd",
                  }}
                >
                  <div
                    style={{
                      color: "#344054",
                      fontSize: "13px",
                      lineHeight: 1.4,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {normalizeErrorMessage(message)}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default SelectedRowErrorPanel;
